import 'react';
import {Box, Stack, Typography} from '@mui/material';
import {InputField} from '@/pages/components/common';
import {AuditProgramSectionVO} from '@/services/auditService/auditProgramService';
import {C83_PAGE} from '@/pages/components/audit-program/c-section/constants';
import ProgramSectionHeader from '@/pages/components/audit-program/a-section/ProgramSectionHeader';

export interface C83AssertionRiskViewProps {
    header: AuditProgramSectionVO;
    values: Record<string, string>;
    onChange: (key: string, value: string) => void;
}

function Field({
    label,
    value,
    onChange,
    multiline,
}: {
    label: string;
    value: string;
    onChange: (v: string) => void;
    multiline?: boolean;
}) {
    return (
        <Stack spacing={0.5} sx={{mb: 1}}>
            <Typography variant="caption" color="text.secondary">{label}</Typography>
            <InputField
                type="text"
                fullWidth
                compact
                align="left"
                multiline={multiline}
                minRows={multiline ? 2 : 1}
                value={value}
                onChange={(e) => onChange(e.target.value)}
            />
        </Stack>
    );
}

/** Excel C8.3 — Assertion Risk Action Plan (simplified editable cards). */
export default function C83AssertionRiskView({header, values, onChange}: C83AssertionRiskViewProps) {
    const f = (id: string, k: string) => values[`${id}.${k}`] ?? '';

    return (
        <Box sx={{p: 2, maxWidth: 960}}>
            <ProgramSectionHeader header={header} variant="a13" refCode="C8.3" values={values} onChange={onChange}/>
            <Typography variant="subtitle1" fontWeight={700} sx={{textAlign: 'center', mb: 1, mt: 0.5}}>
                {C83_PAGE.title}
            </Typography>
            <Typography variant="body2" sx={{mb: 2, lineHeight: 1.45}}>{C83_PAGE.intro}</Typography>

            {C83_PAGE.risks.map((risk, idx) => {
                const desc = values[`${risk.id}.description`] ?? risk.description;
                return (
                    <Box
                        key={risk.id}
                        sx={{
                            mb: 2.5,
                            p: 1.5,
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1,
                        }}
                    >
                        <Typography variant="body2" fontWeight={700} sx={{mb: 1}}>
                            Risk {idx + 1}
                        </Typography>
                        <Field
                            label="Specific risk affecting client"
                            value={desc}
                            onChange={(v) => onChange(`${risk.id}.description`, v)}
                            multiline
                        />
                        <Field
                            label="Classes of transactions / account balances / disclosures"
                            value={f(risk.id, 'classes')}
                            onChange={(v) => onChange(`${risk.id}.classes`, v)}
                            multiline
                        />
                        <Stack direction={{xs: 'column', sm: 'row'}} spacing={2}>
                            <Box sx={{flex: 1}}>
                                <Field
                                    label="Inherent Risk L/M/H"
                                    value={f(risk.id, 'inherent')}
                                    onChange={(v) => onChange(`${risk.id}.inherent`, v)}
                                />
                            </Box>
                            <Box sx={{flex: 2}}>
                                <Field
                                    label="Management Response"
                                    value={f(risk.id, 'mgmtResponse')}
                                    onChange={(v) => onChange(`${risk.id}.mgmtResponse`, v)}
                                    multiline
                                />
                            </Box>
                        </Stack>
                        <Field
                            label="Comments / WP Ref"
                            value={f(risk.id, 'comments')}
                            onChange={(v) => onChange(`${risk.id}.comments`, v)}
                            multiline
                        />
                    </Box>
                );
            })}
        </Box>
    );
}
